import { useCallback } from "react"
import { notify } from "shared/ui/Notifications/notify"
import {
  Slide,
  Template,
  DashboardConfigChange,
} from "types/ReportingDashboard/reporting.types"
import {
  SlideElement,
  DataElementConfig,
} from "types/ReportingDashboard/elements.types"

/**
 * Merged slide content structure
 */
interface MergedSlideContent {
  elements: SlideElement[]
  tableConfigs: Record<string, DataElementConfig>
}

/**
 * Parameters for the useElementManagement hook
 */
interface ElementManagementParams {
  /** Currently active slide object */
  activeSlide: Slide | undefined
  /** ID of the currently active slide */
  activeSlideId: string | null
  /** Whether a template is currently being edited */
  isEditingTemplate: boolean
  /** ID of the template being edited */
  editingTemplate: string | null
  /** Current array of slides */
  currentSlides: Slide[]
  /** Object containing all templates */
  currentTemplates: Record<string, Template>
  /** Callback to update dashboard configuration */
  onDashboardConfigChange: (change: DashboardConfigChange) => void
  /** Merged content for context */
  mergedSlideContent: MergedSlideContent
}

/**
 * Return type for the useElementManagement hook
 */
interface ElementManagementReturn {
  /** Function to handle changes to elements array from canvas */
  handleElementsChange: (newElementsFromCanvas: SlideElement[]) => void
  /** Function to handle table configuration updates */
  handleTableConfigUpdate: (
    elementId: string,
    config: DataElementConfig | null
  ) => void
  /** Function to handle element deletion */
  handleElementDelete: (elementIdToDelete: string) => void
}

/**
 * Custom hook for managing element operations including creation, modification, deletion,
 * and table configuration management.
 *
 * Handles both template and slide element operations with appropriate context switching.
 * Manages table configurations with smart change detection to prevent unnecessary updates.
 * Provides user feedback for operations and enforces business rules (e.g., template element restrictions).
 *
 * @function useElementManagement
 * @param params - Configuration object for element management
 * @returns Object containing element management functions
 */
export const useElementManagement = ({
  activeSlide,
  activeSlideId,
  isEditingTemplate,
  editingTemplate,
  currentSlides,
  currentTemplates,
  onDashboardConfigChange,
  mergedSlideContent,
}: ElementManagementParams): ElementManagementReturn => {
  /**
   * Handles changes to the elements array from the canvas.
   * Routes updates to either template or slide based on current editing mode.
   * Filters template elements when updating slide-specific elements.
   *
   * @function handleElementsChange
   * @param newElementsFromCanvas - Updated array of elements from the canvas
   */
  const handleElementsChange = useCallback(
    (newElementsFromCanvas: SlideElement[]): void => {
      // Rebuild inside a functional updater so slides/templates come from the
      // CURRENT state, not a render snapshot — a concurrent async write (e.g.
      // a chart's debounced image-config save) must not be clobbered, and
      // vice versa this write must not clobber it.
      if (isEditingTemplate && editingTemplate) {
        // Template editing mode: update template elements
        onDashboardConfigChange((prev) => {
          const template = prev.templates?.[editingTemplate]
          if (!template) return null

          return {
            templates: {
              ...prev.templates,
              [editingTemplate]: {
                ...template,
                elements: newElementsFromCanvas,
                timestamp: Date.now(),
              },
            },
          }
        })
      } else {
        // Slide editing mode: update slide-specific elements only
        if (!activeSlide) return

        const slideSpecificElementsInUpdate: SlideElement[] =
          newElementsFromCanvas.filter(
            (el: SlideElement) => !el.isTemplateElement
          )

        onDashboardConfigChange((prev) => ({
          slides: (prev.slides || []).map((slide: Slide) =>
            slide.id === activeSlideId
              ? { ...slide, slideElements: slideSpecificElementsInUpdate }
              : slide
          ),
        }))
      }
    },
    [
      activeSlide,
      isEditingTemplate,
      editingTemplate,
      activeSlideId,
      onDashboardConfigChange,
    ]
  )

  /**
   * Handles table configuration updates with smart change detection.
   * Compares configurations excluding tableData to prevent unnecessary updates.
   * Routes updates to either template or slide based on current editing mode.
   *
   * @function handleTableConfigUpdate
   * @param elementId - ID of the element whose table config is being updated
   * @param config - New table configuration (null to remove)
   */
  const handleTableConfigUpdate = useCallback(
    (elementId: string, config: DataElementConfig | null): void => {
      // Helper function to safely extract config without tableData
      const getConfigWithoutTableData = (
        cfg: DataElementConfig | null | undefined
      ) => {
        if (!cfg || typeof cfg !== "object") return {}
        if ("tableData" in cfg) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { tableData, ...rest } = cfg as DataElementConfig & {
            tableData?: unknown
          }
          return rest
        }
        return cfg
      }

      // Change detection runs against the render snapshot (only gates whether
      // we attempt a write); the actual rebuild happens inside a functional
      // updater against CURRENT state. This call frequently arrives seconds
      // late (the chart-image pipeline's debounced saveConfig) — rebuilding
      // slides from the snapshot here used to revert element positions the
      // user changed in the meantime ("element bounces back after drag").
      const applyConfig = (
        cfgs: Record<string, DataElementConfig>
      ): Record<string, DataElementConfig> => {
        const next = { ...cfgs, [elementId]: config! }
        if (config === null || config === undefined) delete next[elementId]
        return next
      }

      if (isEditingTemplate && editingTemplate) {
        // Template editing mode: update template table configs
        const snapshotTemplate: Template | undefined =
          currentTemplates[editingTemplate]
        if (!snapshotTemplate) return

        const existingConfig: DataElementConfig | undefined = (
          snapshotTemplate.tableConfigs || {}
        )[elementId]
        if (
          JSON.stringify(getConfigWithoutTableData(config)) ===
          JSON.stringify(getConfigWithoutTableData(existingConfig))
        ) {
          return // No actual change — skip the update entirely
        }

        onDashboardConfigChange((prev) => {
          const template = prev.templates?.[editingTemplate]
          if (!template) return null
          return {
            templates: {
              ...prev.templates,
              [editingTemplate]: {
                ...template,
                tableConfigs: applyConfig(template.tableConfigs || {}),
                timestamp: Date.now(),
              },
            },
          }
        })
      } else {
        // Slide editing mode: update slide table configs
        if (!activeSlide) return

        const snapshotSlide: Slide | undefined = currentSlides.find(
          (s: Slide) => s.id === activeSlideId
        )
        if (!snapshotSlide) return

        const existingConfig: DataElementConfig | undefined = (
          snapshotSlide.slideTableConfigs || {}
        )[elementId]
        if (
          JSON.stringify(getConfigWithoutTableData(config)) ===
          JSON.stringify(getConfigWithoutTableData(existingConfig))
        ) {
          return // No actual change — skip the update entirely
        }

        onDashboardConfigChange((prev) => ({
          slides: (prev.slides || []).map((slide: Slide) =>
            slide.id === activeSlideId
              ? {
                  ...slide,
                  slideTableConfigs: applyConfig(
                    slide.slideTableConfigs || {}
                  ),
                }
              : slide
          ),
        }))
      }
    },
    [
      activeSlide,
      isEditingTemplate,
      editingTemplate,
      activeSlideId,
      currentSlides,
      currentTemplates,
      onDashboardConfigChange,
    ]
  )

  /**
   * Handles element deletion with appropriate context and user feedback.
   * Prevents deletion of template elements from slide view.
   * Cleans up associated table configurations when deleting elements.
   *
   * @function handleElementDelete
   * @param elementIdToDelete - ID of the element to delete
   */
  const handleElementDelete = useCallback(
    (elementIdToDelete: string): void => {
      if (!elementIdToDelete) return

      // User feedback is decided against the render snapshot; the state
      // rebuild happens inside a functional updater (updaters must stay pure
      // — no message.* calls in there).
      if (isEditingTemplate && editingTemplate) {
        // Template editing mode: delete from template
        if (!currentTemplates[editingTemplate]) return

        notify.toast.success("Element deleted from template.")
        onDashboardConfigChange((prev) => {
          const template = prev.templates?.[editingTemplate]
          if (!template) return null

          const updatedElements: SlideElement[] = (
            template.elements || []
          ).filter((el: SlideElement) => el.id !== elementIdToDelete)

          // Clean up associated table configurations
          const updatedTableCfgs: Record<string, DataElementConfig> = {
            ...(template.tableConfigs || {}),
          }
          delete updatedTableCfgs[elementIdToDelete]

          return {
            templates: {
              ...prev.templates,
              [editingTemplate]: {
                ...template,
                elements: updatedElements,
                tableConfigs: updatedTableCfgs,
                timestamp: Date.now(),
              },
            },
          }
        })
      } else {
        // Slide editing mode: delete from slide (slide-specific elements only)
        if (!activeSlideId) return

        const snapshotSlide: Slide | undefined = currentSlides.find(
          (s: Slide) => s.id === activeSlideId
        )
        const existsOnSlide: boolean = (
          snapshotSlide?.slideElements || []
        ).some((el: SlideElement) => el.id === elementIdToDelete)

        if (!existsOnSlide) {
          const isActuallyTemplateElement: boolean | undefined =
            mergedSlideContent.elements.find(
              (el: SlideElement) => el.id === elementIdToDelete
            )?.isTemplateElement

          if (isActuallyTemplateElement) {
            notify.toast.info(
              "Cannot delete template elements from the slide view. Edit the template instead."
            )
          } else {
            notify.toast.warning(
              `Element ${elementIdToDelete} not found on this slide.`
            )
          }
          return
        }

        notify.toast.success("Element deleted from slide.")
        onDashboardConfigChange((prev) => ({
          slides: (prev.slides || []).map((slide: Slide) => {
            if (slide.id !== activeSlideId) return slide

            const newSlideElements: SlideElement[] = (
              slide.slideElements || []
            ).filter((el: SlideElement) => el.id !== elementIdToDelete)

            // Clean up associated table configurations
            const newSlideTableCfgs: Record<string, DataElementConfig> = {
              ...(slide.slideTableConfigs || {}),
            }
            delete newSlideTableCfgs[elementIdToDelete]

            return {
              ...slide,
              slideElements: newSlideElements,
              slideTableConfigs: newSlideTableCfgs,
            }
          }),
        }))
      }
    },
    [
      activeSlideId,
      isEditingTemplate,
      editingTemplate,
      mergedSlideContent.elements,
      currentSlides,
      currentTemplates,
      onDashboardConfigChange,
    ]
  )

  return {
    handleElementsChange,
    handleTableConfigUpdate,
    handleElementDelete,
  }
}
